/**
 * The My Saved Views wizard (#1144, PRD §5.2) — the ONLY way to create or
 * save a view (D5).
 *
 * Steps: Filters → Workspaces → Columns → Order → Name.
 * On the Bill Tracking tab the Workspaces step is skipped entirely (R6a/D3):
 * the view applies to that workspace alone.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import Button from '../Button';
import { WarningIcon } from '../icons';
import {
  BillFilterFields,
  type BillFilterFieldsProps,
} from '../legislation/LegislationFilters';
import { CURRENT_SESSION, useSessions } from '../../hooks/useSessions';
import { HelpTrigger } from '../../help';
import { toFilterArray, type BillFilters } from '../../hooks/useBillFilters';
import type { SavedView, SavedViewColumns, SavedViewColumnEntry, BillActionKey } from '../../types/savedViews';
import { emptySavedViewColumns } from '../../types/savedViews';
import {
  BILL_COLUMN_CATALOG,
  BILL_ACTIONS,
  WORKSPACE_COLUMN_CATALOG,
  customFieldCatalog,
  entriesFromSelection,
  pruneUnknownColumns,
  type CatalogColumn,
  type CustomFieldDefinitionLite,
} from './billColumnCatalog';
import {
  createSavedView,
  updateSavedView,
  type SaveViewPayload,
} from '../../services/savedViewsService';

export interface WizardWorkspaceOption {
  id: string;
  name: string;
}

export interface WizardOrgOption {
  id: string;
  name: string;
}

export interface SavedViewWizardProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the saved row; the caller refreshes its list and applies it. */
  onSaved: (view: SavedView) => void;
  /** Editing pre-fills every step (R9); null creates. */
  existingView?: SavedView | null;
  /** Seed for a new view — the table's current filters/search (still wizard-authored). */
  seedFilters: BillFilters;
  seedSearchQuery: string;
  /**
   * Seed for the "Search full bill text" checkbox — the page's current search
   * scope. Without this the checkbox reset to off on every new view, silently
   * dropping the one filter parameter that didn't live inside seedFilters
   * (user report, 2026-08-29). Visible and editable on the Filters step like
   * every other seeded value.
   */
  seedFullTextSearch?: boolean;
  /** Workspaces the user can access, for step 2. */
  workspaceOptions: WizardWorkspaceOption[];
  /**
   * Orgs the author ADMINISTERS — one of them owns the view (PRD §15 D-A).
   * With exactly one, it is chosen silently and the Organization step never
   * renders; with several, the step opens the wizard.
   */
  adminOrgOptions: WizardOrgOption[];
  /** Bill Tracking tab: locks the view to this workspace and skips step 2 (R6a). */
  lockedWorkspaceId?: string;
  /** Org custom fields applicable to bills (already fetched by the caller). */
  customFieldDefs: CustomFieldDefinitionLite[];
  /** owner_org_id -> org name, used only to disambiguate colliding field names. */
  customFieldOrgNames?: Record<string, string>;
  /**
   * Saved with the view as columns.trackedBillsOnly. The toolbar's
   * Tracked/All toggle is the ONLY visible control for this flag — the
   * wizard just snapshots it at save (the step-1 checkbox was a second
   * control for the same setting and confused).
   */
  trackedBillsOnly?: boolean;
  /** Rendering props for the filters step, passed through to BillFilterFields. */
  filterFieldProps: Omit<
    BillFilterFieldsProps,
    'filters' | 'onFilterChange' | 'onCommitteeFilterChange' | 'labelClass' | 'inputClass'
  >;
}

type StepId = 'organization' | 'filters' | 'workspaces' | 'columns' | 'order' | 'name';

const STEP_TITLES: Record<StepId, string> = {
  organization: 'Organization',
  filters: 'Filters',
  workspaces: 'Workspaces',
  columns: 'Columns',
  order: 'Column order',
  name: 'Name & save',
};

/** Workspace-level columns belong to a workspace, not the bill — said at the point of choice (R7). */
const WORKSPACE_SCOPE_MESSAGE =
  'Position, Notes, and Custom Fields belong to a workspace, not to the bill. Each workspace you select gets its own row beneath the bill.';

const SavedViewWizard: React.FC<SavedViewWizardProps> = ({
  isOpen,
  onClose,
  onSaved,
  existingView = null,
  seedFilters,
  seedSearchQuery,
  seedFullTextSearch = false,
  workspaceOptions,
  lockedWorkspaceId,
  customFieldDefs,
  customFieldOrgNames,
  adminOrgOptions,
  trackedBillsOnly = false,
  filterFieldProps,
}) => {
  const steps: StepId[] = useMemo(() => {
    const base: StepId[] = lockedWorkspaceId
      ? ['filters', 'columns', 'order', 'name']
      : ['filters', 'workspaces', 'columns', 'order', 'name'];
    // Ownership is only a question when the author administers several orgs
    // (D-A); a single-org admin's org is chosen silently below.
    return adminOrgOptions.length > 1 ? ['organization', ...base] : base;
  }, [lockedWorkspaceId, adminOrgOptions.length]);

  const [stepIndex, setStepIndex] = useState(0);
  const [filters, setFilters] = useState<BillFilters>(seedFilters);

  /*
    Relative vs pinned (PRD §5.D4). The stored session value is the scope: the
    'current' sentinel (or no session at all) follows the current session; an
    explicit key stays put.
  */
  const { sessions } = useSessions();
  const isRelativeSessionScope =
    filters.session.length === 0 || filters.session.every((v) => v === CURRENT_SESSION);
  const pinnedSessionLabel = filters.session
    .map((key) => sessions.find((sn) => sn.sessionKey === key)?.label ?? key)
    .join(', ');
  /*
    Search is a VISIBLE wizard field, not a silent capture. The first release
    copied the page's search box into the view without showing it, so a stray
    term (e.g. "heating") rode along invisibly and zeroed the results with no
    clue why. Whatever the view will search, the user sees and edits here.
  */
  const [searchText, setSearchText] = useState('');
  const [fullTextSearch, setFullTextSearch] = useState(false);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [billCols, setBillCols] = useState<SavedViewColumnEntry[]>([]);
  const [actionKeys, setActionKeys] = useState<BillActionKey[]>([]);
  const [workspaceCols, setWorkspaceCols] = useState<SavedViewColumnEntry[]>([]);
  const [subRecordsExpanded, setSubRecordsExpanded] = useState(true);
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [makePrimary, setMakePrimary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsCatalog: CatalogColumn[] = useMemo(
    () => [...WORKSPACE_COLUMN_CATALOG, ...customFieldCatalog(customFieldDefs, customFieldOrgNames)],
    [customFieldDefs, customFieldOrgNames]
  );
  const knownWsColIds = useMemo(() => new Set(wsCatalog.map((c) => c.colId)), [wsCatalog]);
  /* No custom fields defined => no asterisks, so the footnote would explain nothing. */
  const hasCustomFieldColumns = useMemo(() => wsCatalog.some((c) => c.isCustomField), [wsCatalog]);
  const knownBillColIds = useMemo(
    () => new Set(BILL_COLUMN_CATALOG.map((c) => c.colId)),
    []
  );

  // (Re)seed all state when the wizard opens — edit pre-fills from the view (R9).
  useEffect(() => {
    if (!isOpen) return;
    setStepIndex(0);
    setError(null);
    setSaving(false);
    if (existingView) {
      /*
        Views saved before multi-select hold strings ('2025', 'Education'), and
        the spread is an unchecked cast — `filters.session.every(...)` below
        then throws (Sentry FRONTEND-2H, #1450). Coerce every multi-select key
        the same way useSavedViewState does when a view is applied.
      */
      const saved = existingView.filters as Partial<BillFilters> & Record<string, unknown>;
      setFilters({
        ...seedFilters,
        ...saved,
        subject: toFilterArray('subject' in saved ? saved.subject : seedFilters.subject, ['All subjects']),
        status: toFilterArray('status' in saved ? saved.status : seedFilters.status),
        sponsor: toFilterArray('sponsor' in saved ? saved.sponsor : seedFilters.sponsor),
        committee_id: toFilterArray('committee_id' in saved ? saved.committee_id : seedFilters.committee_id),
        session: toFilterArray('session' in saved ? saved.session : seedFilters.session),
      });
      const cols = existingView.columns ?? emptySavedViewColumns();
      // Stale colIds (deleted custom field, removed column) are pruned, not rendered (§11).
      setBillCols(pruneUnknownColumns(cols.billColumns, knownBillColIds));
      setActionKeys((cols.actions ?? []) as BillActionKey[]);
      setWorkspaceCols(pruneUnknownColumns(cols.workspaceColumns, knownWsColIds));
      setSubRecordsExpanded(cols.subRecordsExpanded);
      setSelectedWorkspaceIds(existingView.workspaceIds);
      // Ownership never moves on edit; fall back for pre-§15 rows.
      setOrganizationId(existingView.organizationId || adminOrgOptions[0]?.id || '');
      setName(existingView.name);
      setMakePrimary(existingView.isPrimary);
      setSearchText(existingView.searchQuery || '');
      setFullTextSearch(
        (existingView.filters as Record<string, unknown>)?.legislationSearchScope === 'fulltext'
      );
    } else {
      setFilters(seedFilters);
      setBillCols([]);
      setActionKeys([]);
      setWorkspaceCols([]);
      setSubRecordsExpanded(true);
      setSelectedWorkspaceIds([]);
      setOrganizationId(adminOrgOptions.length === 1 ? adminOrgOptions[0].id : '');
      setName('');
      setMakePrimary(false);
      setSearchText(seedSearchQuery || '');
      setFullTextSearch(seedFullTextSearch);
    }
    // seedFilters/seedSearchQuery/seedFullTextSearch intentionally sampled at open time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingView]);

  const step = steps[stepIndex];
  const effectiveWorkspaceIds = lockedWorkspaceId ? [lockedWorkspaceId] : selectedWorkspaceIds;

  /* R8: workspace-level columns with no workspaces would never render. Block at save. */
  const workspaceColsWithoutWorkspaces =
    workspaceCols.length > 0 && effectiveWorkspaceIds.length === 0;

  const canGoNext = (() => {
    if (step === 'name') return false;
    if (step === 'organization') return !!organizationId;
    return true;
  })();

  const canSave =
    step === 'name' &&
    name.trim().length > 0 &&
    !!organizationId &&
    !workspaceColsWithoutWorkspaces &&
    !saving;

  const handleFilterChange = <K extends keyof BillFilters>(key: K, value: BillFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleCommitteeFilterChange = (committeeIds: string[]) => {
    setFilters((prev) => ({ ...prev, committee_id: committeeIds, committee: '' }));
  };

  const toggleCatalogColumn = (
    catalog: CatalogColumn[],
    current: SavedViewColumnEntry[],
    setter: (next: SavedViewColumnEntry[]) => void,
    colId: string
  ) => {
    const selected = new Set(current.map((c) => c.colId));
    if (selected.has(colId)) selected.delete(colId);
    else selected.add(colId);
    // Preserve any explicit order already set; new columns append in catalog order.
    const kept = current.filter((c) => selected.has(c.colId));
    const keptIds = new Set(kept.map((c) => c.colId));
    const added = entriesFromSelection(catalog, selected).filter((c) => !keptIds.has(c.colId));
    setter([...kept, ...added]);
  };

  const move = (
    list: SavedViewColumnEntry[],
    setter: (next: SavedViewColumnEntry[]) => void,
    index: number,
    delta: -1 | 1
  ) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    setter(next);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      /* An Actions column with nothing in it renders empty — drop it instead. */
      const effectiveBillCols =
        actionKeys.length === 0 ? billCols.filter((c) => c.colId !== 'actions') : billCols;
      const columns: SavedViewColumns = {
        version: 1,
        billColumns: effectiveBillCols,
        workspaceColumns: workspaceCols,
        subRecordsExpanded,
        inlineSingleWorkspace: true,
        trackedBillsOnly,
        ...(effectiveBillCols.some((c) => c.colId === 'actions') && { actions: actionKeys }),
      };
      /*
        legislationSearchScope rides inside filters — the pre-existing saved-
        filter convention that applySavedView already reads. Stored only when
        fulltext, so metadata (the default) leaves no residue.
      */
      const filtersPayload: Record<string, unknown> = {
        ...(filters as unknown as Record<string, unknown>),
      };
      if (fullTextSearch) filtersPayload.legislationSearchScope = 'fulltext';
      else delete filtersPayload.legislationSearchScope;

      const payload: SaveViewPayload = {
        name: name.trim(),
        organizationId,
        filters: filtersPayload,
        searchQuery: searchText.trim(),
        columns,
        workspaceIds: effectiveWorkspaceIds,
        isPrimary: makePrimary,
      };
      const saved = existingView
        ? await updateSavedView(existingView.id, payload)
        : await createSavedView(payload);
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the view');
    } finally {
      setSaving(false);
    }
  };

  const checkboxRow = (
    key: string,
    label: string,
    checked: boolean,
    onToggle: () => void,
    /* Custom fields carry an asterisk keyed to the footnote below the card. */
    isCustomField = false
  ) => (
    <label
      key={key}
      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary cursor-pointer"
    >
      <input type="checkbox" checked={checked} onChange={onToggle} className="accent-primary-700" />
      <span className="truncate">
        {label}
        {isCustomField && (
          <span
            className="ml-0.5 text-text-tertiary dark:text-text-dark-tertiary"
            /* Read out rather than voiced as "asterisk": the mark is only
               meaningful paired with the footnote, which a screen reader
               reaches long after this row. */
            aria-label="Custom field"
            title="Custom field"
          >
            *
          </span>
        )}
      </span>
    </label>
  );

  const orderList = (
    title: string,
    list: SavedViewColumnEntry[],
    setter: (next: SavedViewColumnEntry[]) => void,
    labelFor: (colId: string) => string
  ) => (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
        {title}
      </h4>
      {list.length === 0 ? (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary px-2 py-1">None selected</p>
      ) : (
        <ul className="space-y-1">
          {list.map((entry, i) => (
            <li
              key={entry.colId}
              className="flex items-center justify-between gap-2 rounded-md border border-border-light dark:border-border-dark px-2.5 py-1.5 text-sm"
            >
              <span className="truncate">{labelFor(entry.colId)}</span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={`Move ${labelFor(entry.colId)} up`}
                  disabled={i === 0}
                  onClick={() => move(list, setter, i, -1)}
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary disabled:opacity-30"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${labelFor(entry.colId)} down`}
                  disabled={i === list.length - 1}
                  onClick={() => move(list, setter, i, 1)}
                  className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary disabled:opacity-30"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const labelForColId = (colId: string): string => {
    const all = [...BILL_COLUMN_CATALOG, ...wsCatalog];
    return all.find((c) => c.colId === colId)?.label ?? colId;
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={existingView ? `Edit view: ${existingView.name}` : 'New saved view'}
      /* xl (max-w-4xl): lg's 672px forces a horizontal scrollbar under the
         three-column filter grid at 100% zoom. */
      size="xl"
    >
      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-4 text-xs" aria-label="Wizard steps">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary">›</span>}
            <button
              type="button"
              onClick={() => setStepIndex(i)}
              className={`px-2 py-1 rounded ${
                i === stepIndex
                  ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 font-semibold'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:underline'
              }`}
              aria-current={i === stepIndex ? 'step' : undefined}
            >
              {STEP_TITLES[s]}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="min-h-[320px]">
        {step === 'organization' && (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Which organization owns this view? Its admins can edit it, and its
              members see it in the workspaces you pick. You administer more
              than one, so this needs saying.
            </p>
            <div className="rounded-lg border border-border-light dark:border-border-dark p-1.5">
              {adminOrgOptions.map((org) => (
                <label
                  key={org.id}
                  className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary cursor-pointer"
                >
                  <input
                    type="radio"
                    name="saved-view-organization"
                    checked={organizationId === org.id}
                    onChange={() => setOrganizationId(org.id)}
                    className="accent-primary-700"
                    /* Ownership is fixed after creation — moving a view between
                       orgs would silently change who can edit and see it. */
                    disabled={!!existingView}
                  />
                  <span className="truncate">{org.name}</span>
                </label>
              ))}
            </div>
            {existingView && (
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                A view's organization is set when it is created and cannot change.
              </p>
            )}
          </div>
        )}

        {step === 'filters' && (
          <div className="space-y-4">
            {/*
              Session scope, stated rather than inferred (PRD §5.D4).

              The scope IS the stored session value — no separate field. A view
              holding the 'current' sentinel follows the current session for as
              long as it exists; one holding explicit keys stays on them. Adding
              a second `sessionScope` field to say the same thing would be two
              representations of one fact, which is how they drift apart.

              Views saved before the sentinel existed hold explicit years, so
              they read as pinned and behave exactly as they always did — which
              is why this needed no data migration.
            */}
            <div className="rounded-lg border border-border-light px-3 py-2 text-xs dark:border-border-dark">
              <span className="font-medium text-text-secondary dark:text-text-dark-secondary">
                Session scope{' '}
                <HelpTrigger contentKey="saved-view-session-scope" size="sm" />:{' '}
              </span>
              {isRelativeSessionScope ? (
                <span className="text-text-primary dark:text-text-dark-primary">
                  Follows the current session — this view will show the next session's
                  bills automatically when it convenes.
                </span>
              ) : (
                <span className="text-text-primary dark:text-text-dark-primary">
                  Pinned to {pinnedSessionLabel} — this view stays on that session even
                  after a new one convenes.
                </span>
              )}
              <span className="ml-1 text-text-tertiary dark:text-text-dark-tertiary">
                Change it with the Session filter below.
              </span>
            </div>

            <div>
              <label
                className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1"
                htmlFor="saved-view-search"
              >
                Keyword search
              </label>
              <input
                id="saved-view-search"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search bills by number, title, or content…"
                className="input w-full text-sm"
              />
              <label className="mt-1.5 flex items-center gap-2 text-xs text-text-secondary dark:text-text-dark-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={fullTextSearch}
                  onChange={(e) => setFullTextSearch(e.target.checked)}
                  className="accent-primary-700"
                />
                <span>
                  Search full bill text
                  <span className="ml-1 text-text-tertiary dark:text-text-dark-tertiary">
                    — otherwise only number, title, and summary are searched
                  </span>
                </span>
              </label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <BillFilterFields
                {...filterFieldProps}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCommitteeFilterChange={handleCommitteeFilterChange}
                labelClass="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1"
                inputClass="input text-sm"
              />
            </div>
          </div>
        )}

        {step === 'workspaces' && (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {WORKSPACE_SCOPE_MESSAGE}
            </p>
            {workspaceOptions.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                You have no workspaces available.
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-border-light dark:border-border-dark p-1.5">
                {/* Select all: indeterminate while only some are ticked. */}
                <label className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-semibold text-text-primary dark:text-text-dark-primary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary cursor-pointer border-b border-border-light/60 dark:border-border-dark/50 mb-1">
                  <input
                    type="checkbox"
                    aria-label="Select all workspaces"
                    checked={
                      workspaceOptions.length > 0 &&
                      selectedWorkspaceIds.length === workspaceOptions.length
                    }
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedWorkspaceIds.length > 0 &&
                          selectedWorkspaceIds.length < workspaceOptions.length;
                      }
                    }}
                    onChange={() =>
                      setSelectedWorkspaceIds(
                        selectedWorkspaceIds.length === workspaceOptions.length
                          ? []
                          : workspaceOptions.map((ws) => ws.id)
                      )
                    }
                    className="accent-primary-700"
                  />
                  <span>Select all ({workspaceOptions.length})</span>
                </label>
                {workspaceOptions.map((ws) =>
                  checkboxRow(ws.id, ws.name, selectedWorkspaceIds.includes(ws.id), () =>
                    setSelectedWorkspaceIds((prev) =>
                      prev.includes(ws.id) ? prev.filter((id) => id !== ws.id) : [...prev, ws.id]
                    )
                  )
                )}
              </div>
            )}
          </div>
        )}

        {step === 'columns' && (
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
                Bill columns
              </h4>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
                Bill number is always shown.
              </p>
              <div className="rounded-lg border border-border-light dark:border-border-dark p-1.5">
                {BILL_COLUMN_CATALOG.map((c) => {
                  const checked = billCols.some((e) => e.colId === c.colId);
                  const row = checkboxRow(c.colId, c.label, checked, () => {
                    toggleCatalogColumn(BILL_COLUMN_CATALOG, billCols, setBillCols, c.colId);
                    // Selecting Actions starts with every action on; unchecking clears them.
                    if (c.colId === 'actions') {
                      setActionKeys(checked ? [] : BILL_ACTIONS.map((action) => action.key));
                    }
                  });
                  if (c.colId !== 'actions' || !checked) return row;
                  return (
                    <React.Fragment key="actions-group">
                      {row}
                      <div className="ml-7 border-l border-border-light dark:border-border-dark pl-2">
                        {BILL_ACTIONS.map((action) => (
                          <label
                            key={action.key}
                            title={action.description}
                            className="flex items-center gap-2.5 rounded-md px-2 py-1 text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary cursor-help"
                          >
                            <input
                              type="checkbox"
                              checked={actionKeys.includes(action.key)}
                              onChange={() =>
                                setActionKeys((prev) =>
                                  prev.includes(action.key)
                                    ? prev.filter((k) => k !== action.key)
                                    : [...prev, action.key]
                                )
                              }
                              className="accent-primary-700"
                            />
                            <span className="truncate">{action.label}</span>
                          </label>
                        ))}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary mb-1.5">
                Workspace columns
              </h4>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
                {WORKSPACE_SCOPE_MESSAGE}
              </p>
              <div className="rounded-lg border border-border-light dark:border-border-dark p-1.5">
                {wsCatalog.map((c) =>
                  checkboxRow(
                    c.colId,
                    c.label,
                    workspaceCols.some((e) => e.colId === c.colId),
                    () => toggleCatalogColumn(wsCatalog, workspaceCols, setWorkspaceCols, c.colId),
                    c.isCustomField
                  )
                )}
              </div>
              {hasCustomFieldColumns && (
                <p className="mt-1.5 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  * Custom field
                </p>
              )}
              {workspaceColsWithoutWorkspaces && !lockedWorkspaceId && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <WarningIcon className="w-4 h-4 flex-none" />
                  These columns need at least one workspace — go back to the Workspaces step and
                  pick one, or unselect them.
                </p>
              )}
            </div>
          </div>
        )}

        {step === 'order' && (
          <div className="space-y-5">
            {orderList('Bill columns', billCols, setBillCols, labelForColId)}
            {orderList('Workspace columns', workspaceCols, setWorkspaceCols, labelForColId)}
            {workspaceCols.length > 0 && (
              <label className="flex items-center gap-2.5 text-sm text-text-secondary dark:text-text-dark-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={subRecordsExpanded}
                  onChange={(e) => setSubRecordsExpanded(e.target.checked)}
                  className="accent-primary-700"
                />
                Start with workspace rows expanded
              </label>
            )}
          </div>
        )}

        {step === 'name' && (
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1" htmlFor="saved-view-name">
                View name
              </label>
              <input
                id="saved-view-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                className="input w-full"
                placeholder="e.g. Education bills across workspaces"
              />
            </div>
            <label className="flex items-center gap-2.5 text-sm text-text-secondary dark:text-text-dark-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={makePrimary}
                onChange={(e) => setMakePrimary(e.target.checked)}
                className="accent-primary-700"
              />
              Make this my primary view (loads by default)
            </label>
            {workspaceColsWithoutWorkspaces && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <WarningIcon className="w-4 h-4 flex-none" />
                This view selects workspace columns but no workspaces, so those columns would
                never show anything. Pick a workspace or remove them before saving.
              </p>
            )}
            {error && <p className="text-sm text-error-600 dark:text-error-400">{error}</p>}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="mt-5 flex items-center justify-between border-t border-border-light dark:border-border-dark pt-3">
        <Button variant="secondary" onClick={() => (stepIndex === 0 ? onClose() : setStepIndex(stepIndex - 1))}>
          {stepIndex === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step !== 'name' ? (
          <Button variant="primary" onClick={() => setStepIndex(stepIndex + 1)} disabled={!canGoNext}>
            Next
          </Button>
        ) : (
          <Button variant="primary" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving…' : existingView ? 'Save view' : 'Create view'}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default SavedViewWizard;
